import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUB = process.env.PUBLIC_URL || "";

const fmt$ = (n, dp = 2) =>
    n == null ? "—" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const fmtPct = (n) => (n == null ? "—" : `${(Number(n) * 100).toFixed(2)}%`);
const fmtPctAbs = (n) => (n == null ? "—" : `${Number(n) > 0 ? "+" : ""}${Number(n).toFixed(2)}%`);

const stateChip = (s) =>
    s === "NORMAL"
        ? { color: "#2BFF9E", border: "#2BFF9E" }
        : s === "HALTED"
        ? { color: "#FF5470", border: "#FF5470" }
        : { color: "#B98CFF", border: "#B98CFF" };

const modeChip = (m) =>
    m === "live"
        ? { color: "#FF5470", border: "#FF5470" }
        : m === "demo"
        ? { color: "#2DE9FF", border: "#2DE9FF" }
        : { color: "#B98CFF", border: "#B98CFF" };

const TABS = [
    { id: "strategy", label: "Strategy" },
    { id: "monitor", label: "Live Monitor" },
    { id: "report", label: "Daily Report" },
];

export default function OdinDashboard() {
    const [tab, setTab] = useState("monitor");
    const [snap, setSnap] = useState(null);
    const [hist, setHist] = useState([]);
    const [err, setErr] = useState(null);

    const fetchAll = async () => {
        try {
            const [live, history] = await Promise.all([
                axios.get(`${API}/odin/live`),
                axios.get(`${API}/odin/history?hours=24`),
            ]);
            setSnap(live.data.snapshot);
            setHist(history.data.points || []);
            setErr(null);
        } catch (e) {
            setErr(e.message);
        }
    };

    useEffect(() => {
        fetchAll();
        const iv = setInterval(fetchAll, 30000);
        return () => clearInterval(iv);
    }, []);

    return (
        <div className="odin-scope" data-testid="odin-dashboard">
            <div className="mx-auto max-w-[1400px] px-5 pb-24 pt-6 sm:px-8 relative" style={{ zIndex: 1 }}>
                {/* Header */}
                <header className="flex flex-col gap-6 border-b pb-5" style={{ borderColor: "rgba(42,53,80,0.7)" }}>
                    <div className="flex justify-center pt-2 pb-1">
                        <img
                            src={`${PUB}/guild-capital-logo.png`}
                            alt="GUILD Capital"
                            data-testid="guild-logo"
                            className="h-28 w-auto sm:h-36"
                            style={{ filter: "drop-shadow(0 0 34px rgba(255,255,255,0.18))" }}
                        />
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-6">
                            <img
                                src={`${PUB}/odin-character.jpg`}
                                alt="ODIN"
                                data-testid="odin-avatar"
                                className="rounded-full object-cover bg-black"
                                style={{
                                    height: 140,
                                    width: 140,
                                    border: "3px solid rgba(185,140,255,0.6)",
                                    filter: "drop-shadow(0 0 32px rgba(185,140,255,0.9))",
                                }}
                            />
                            <div>
                                <h1 className="font-display font-black tracking-tight text-white text-glow-violet" style={{ fontSize: 76, lineHeight: 1, letterSpacing: -1 }}>
                                    ODIN
                                </h1>
                                <p className="font-mono-plex uppercase tracking-[0.3em] mt-2" style={{ color: "#B98CFF", fontSize: 14, fontWeight: 700, textShadow: "0 0 14px rgba(185,140,255,0.6)" }}>
                                    Guild Gambling Book · Inner-Circle Only
                                </p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-2 px-3 py-1.5" style={{ border: "1px solid rgba(42,53,80,0.7)" }}>
                            <span className="odin-pulse h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#2DE9FF" }} />
                            <span className="font-mono-plex text-[10px] uppercase tracking-[0.2em]" style={{ color: "#FFFFFF" }}>
                                odin_db · isolated from MIDAS
                            </span>
                        </div>
                    </div>

                    {/* Tabs */}
                    <nav className="flex items-center gap-1" data-testid="odin-tabs">
                        {TABS.map((t) => {
                            const active = tab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    data-testid={`tab-${t.id}`}
                                    onClick={() => setTab(t.id)}
                                    className="relative px-5 py-3 font-display text-sm font-bold uppercase tracking-[0.15em] transition-colors duration-200"
                                    style={{
                                        borderBottom: `2px solid ${active ? "#2DE9FF" : "transparent"}`,
                                        color: active ? "#FFFFFF" : "#FFFFFF",
                                        textShadow: active ? "0 0 24px rgba(45,233,255,0.85), 0 0 8px rgba(45,233,255,0.6)" : "none",
                                        boxShadow: active ? "0 6px 20px -8px rgba(45,233,255,0.9)" : "none",
                                        background: "transparent",
                                        border: "none",
                                        borderBottom: `2px solid ${active ? "#2DE9FF" : "transparent"}`,
                                        cursor: "pointer",
                                    }}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </nav>
                </header>

                {/* Content */}
                <main className="pt-8">
                    {err && (
                        <div style={{ color: "#FF5470", padding: 20 }} data-testid="odin-error">
                            Error: {err}
                        </div>
                    )}
                    {!err && !snap && (
                        <div style={{ color: "#FFFFFF", padding: 20 }} data-testid="odin-loading">
                            Waiting for first ODIN ping from Mac...
                        </div>
                    )}
                    {snap && tab === "strategy" && <StrategyTab />}
                    {snap && tab === "monitor" && <LiveMonitorTab snap={snap} hist={hist} />}
                    {snap && tab === "report" && <DailyReportTab snap={snap} hist={hist} />}
                </main>

                <footer className="mt-16 border-t pt-5" style={{ borderColor: "rgba(42,53,80,0.7)" }}>
                    <p className="electric-pulse max-w-3xl font-mono-plex text-xs font-medium leading-relaxed">
                        Guild's gambling book · $10K seed · Regime Trend Gambler · Weekly TSMOM + Donchian breakout · two-pot capital · 3-4x leverage.
                        Automatic guardian armed at all times. Fully isolated in odin_db; MIDAS is never touched.
                    </p>
                </footer>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   LIVE MONITOR — V3 two-pot / state machine / positions / equity feed
   ═══════════════════════════════════════════════════════════════════ */
function LiveMonitorTab({ snap, hist }) {
    const positions = snap.active_positions || [];
    const stale = snap.is_stale;
    const st = stateChip(snap.state);
    const md = modeChip(snap.mode);

    return (
        <div className="odin-fade-up space-y-6">
            {/* Status Strip */}
            <div className="flex flex-wrap gap-3 items-center">
                <Chip label={snap.mode || "—"} color={md.color} border={md.border} testid="odin-mode" />
                <Chip label={snap.state || "—"} color={st.color} border={st.border} testid="odin-state" />
                <Chip label={(snap.current_regime || "—").replaceAll("_", " ")} color="#B98CFF" border="#B98CFF" testid="odin-regime" />
                <Chip
                    label={`${stale ? "STALE" : "LIVE"} · ${snap.minutes_since_update ?? "—"}m`}
                    color={stale ? "#FF5470" : "#2BFF9E"}
                    border={stale ? "#FF5470" : "#2BFF9E"}
                    testid="odin-sync"
                />
            </div>

            <LiveCockpit snap={snap} />

            {/* Slot Machine Performance Grid — bull vs bear month expectations */}
            <div className="neon-panel" style={{ background: "linear-gradient(135deg, rgba(18,18,22,0.98), rgba(12,12,14,0.95))", padding: 26, marginTop: 4, marginBottom: 4 }}>
                <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 18, fontWeight: 700, textShadow: "0 0 12px rgba(45,233,255,0.6)" }}>
                    🎰 Slot Machine Range · Working Pot Monthly Expectations
                </div>
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                    <div className="neon-panel" style={{ padding: "18px 20px", background: "rgba(43,255,158,0.06)", border: "1px solid rgba(43,255,158,0.4)" }} data-testid="slot-bull">
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 8, fontWeight: 700 }}>Bull Months</div>
                        <div className="font-display" style={{ color: "#2BFF9E", fontSize: 38, fontWeight: 800, letterSpacing: 0.5, textShadow: "0 0 26px #2BFF9E, 0 0 44px rgba(43,255,158,0.6)", lineHeight: 1 }}>+40% to +80%</div>
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, marginTop: 10, letterSpacing: "0.05em" }}>Nov 2020 · Feb 2021 · Jan 2024</div>
                    </div>
                    <div className="neon-panel" style={{ padding: "18px 20px", background: "rgba(45,233,255,0.06)", border: "1px solid rgba(45,233,255,0.4)" }} data-testid="slot-deepstrong">
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 8, fontWeight: 700 }}>Deep-Strong Boost</div>
                        <div className="font-display" style={{ color: "#2DE9FF", fontSize: 38, fontWeight: 800, letterSpacing: 0.5, textShadow: "0 0 26px #2DE9FF, 0 0 44px rgba(45,233,255,0.6)", lineHeight: 1 }}>+80% to +120%</div>
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, marginTop: 10, letterSpacing: "0.05em" }}>6x when BTC ADX &gt; 40 · 1-2 per 8y</div>
                    </div>
                    <div className="neon-panel" style={{ padding: "18px 20px", background: "rgba(185,140,255,0.06)", border: "1px solid rgba(185,140,255,0.4)" }} data-testid="slot-avg">
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 8, fontWeight: 700 }}>Trending Average</div>
                        <div className="font-display" style={{ color: "#B98CFF", fontSize: 38, fontWeight: 800, letterSpacing: 0.5, textShadow: "0 0 26px #B98CFF, 0 0 44px rgba(185,140,255,0.6)", lineHeight: 1 }}>+8% to +25%</div>
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, marginTop: 10, letterSpacing: "0.05em" }}>Any normal trending month</div>
                    </div>
                    <div className="neon-panel" style={{ padding: "18px 20px", background: "rgba(255,84,112,0.06)", border: "1px solid rgba(255,84,112,0.4)" }} data-testid="slot-bear">
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", marginBottom: 8, fontWeight: 700 }}>Crash Months</div>
                        <div className="font-display" style={{ color: "#FF5470", fontSize: 38, fontWeight: 800, letterSpacing: 0.5, textShadow: "0 0 26px #FF5470, 0 0 44px rgba(255,84,112,0.6)", lineHeight: 1 }}>-15% to -28%</div>
                        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 11, marginTop: 10, letterSpacing: "0.05em" }}>Mar 2020 COVID · May 2021 · June 2022 LUNA</div>
                    </div>
                </div>
                <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, marginTop: 20, padding: "14px 18px", background: "rgba(255,84,112,0.05)", border: "1px solid rgba(255,84,112,0.3)", letterSpacing: "0.05em", lineHeight: 1.7 }} data-testid="slot-worst-day">
                    <span style={{ color: "#FF5470", fontWeight: 700, letterSpacing: "0.2em" }}>WORST SINGLE DAY EVER · 8.4Y BACKTEST:</span>{" "}
                    <span style={{ color: "#FFFFFF", fontWeight: 800, fontSize: 14 }}>-34.9%</span> on working pot · kill fired · <span style={{ color: "#2BFF9E" }}>absorbed by reserve · zero cascade · working reset to base next 00:00 UTC · bot resumed spinning</span>
                </div>
            </div>

            {/* Reserve / Chip Stack Explanation Panel */}
            <div className="neon-panel" style={{ background: "linear-gradient(135deg, rgba(18,18,22,0.98), rgba(12,12,14,0.95))", padding: 26, marginBottom: 4 }}>
                <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 16, fontWeight: 700, textShadow: "0 0 12px rgba(43,255,158,0.6)" }}>
                    💰 Reserve Chip Stack · Where the Real Money Sits
                </div>
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
                    <div className="font-mono-plex" style={{ padding: "16px 18px", border: "1px solid rgba(43,255,158,0.35)", background: "rgba(43,255,158,0.04)" }}>
                        <div style={{ color: "#2BFF9E", fontSize: 12, letterSpacing: "0.2em", fontWeight: 700, marginBottom: 8, textShadow: "0 0 10px rgba(43,255,158,0.6)" }}>WHAT IT IS</div>
                        <div style={{ color: "#FFFFFF", fontSize: 13, lineHeight: 1.7 }}>Every dollar of profit this bot has ever earned. Never external capital. 100% bot-earned via daily sweeps.</div>
                    </div>
                    <div className="font-mono-plex" style={{ padding: "16px 18px", border: "1px solid rgba(45,233,255,0.35)", background: "rgba(45,233,255,0.04)" }}>
                        <div style={{ color: "#2DE9FF", fontSize: 12, letterSpacing: "0.2em", fontWeight: 700, marginBottom: 8, textShadow: "0 0 10px rgba(45,233,255,0.6)" }}>WHERE IT LIVES</div>
                        <div style={{ color: "#FFFFFF", fontSize: 13, lineHeight: 1.7 }}>Ethena sUSDe (~15% APY) or Bybit Earn (~4% fallback). Real stablecoin, redeemable any minute. Never at risk from the engine.</div>
                    </div>
                    <div className="font-mono-plex" style={{ padding: "16px 18px", border: "1px solid rgba(185,140,255,0.35)", background: "rgba(185,140,255,0.04)" }}>
                        <div style={{ color: "#B98CFF", fontSize: 12, letterSpacing: "0.2em", fontWeight: 700, marginBottom: 8, textShadow: "0 0 10px rgba(185,140,255,0.6)" }}>WITHDRAW ANY DAY</div>
                        <div style={{ color: "#FFFFFF", fontSize: 13, lineHeight: 1.7 }}>Working &gt; $10K by even $1? Sweep it to reserve any minute. Reserve is LP-redeemable daily without touching the working pot. Bot keeps spinning.</div>
                    </div>
                </div>
                <div className="font-mono-plex electric-pulse" style={{ marginTop: 18, fontSize: 13, letterSpacing: "0.15em", color: "#FFFFFF", textAlign: "center", padding: "12px", background: "rgba(45,233,255,0.08)", border: "1px solid rgba(45,233,255,0.4)" }}>
                    ● Chip stack grows monotonically · Working resets daily · Losses never cascade · Zero blowups in 8.4 years
                </div>
            </div>

            {/* Two-Pot KPI Grid */}
            <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <NeonKPI label="Total Capital" value={fmt$(snap.total_capital)} tone="#2DE9FF" sub="working + reserve" testid="odin-total" />
                <NeonKPI label="Working Pot" value={fmt$(snap.working_equity)} tone="#FFFFFF" sub="active trading capital" testid="odin-working" />
                <NeonKPI label="Reserve Pot" value={fmt$(snap.reserve_equity)} tone="#2BFF9E" sub="sacred · Bybit Earn 4%" testid="odin-reserve" />
                <NeonKPI label="Total MaxDD" value={fmtPct(snap.total_capital_maxdd)} tone="#FF5470" sub="total capital drawdown" testid="odin-total-maxdd" />
                <NeonKPI label="Working MaxDD" value={fmtPct(snap.working_maxdd)} tone="#FF5470" sub="engine variance" testid="odin-working-maxdd" />
                <NeonKPI
                    label="Kill Events"
                    value={snap.kill_events_lifetime ?? 0}
                    tone="#B98CFF"
                    sub={`sweeps: ${snap.sweep_events_this_week ?? 0} this wk`}
                    testid="odin-kills"
                />
                {snap.baseline_hwm != null && (
                    <NeonKPI
                        label="Baseline HWM"
                        value={fmt$(snap.baseline_hwm, 0)}
                        tone="#B98CFF"
                        sub={`sweep mode: ${snap.sweep_mode || "fixed"} · next sweep @ ${fmt$((snap.baseline_hwm || 0) * 1.5, 0)}`}
                        testid="odin-baseline-hwm"
                    />
                )}
            </div>

            {/* V4 Daily Hunter tiles (auto-appear when mode_variant=v4) */}
            {snap.mode_variant === "v4" && (
                <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                    <NeonKPI
                        label="Entries Today"
                        value={`${snap.entries_today_count ?? 0} / 3`}
                        tone="#2DE9FF"
                        sub="daily hunter rate limit"
                        testid="odin-entries-today"
                    />
                    <NeonKPI
                        label="Signal Tick Age"
                        value={
                            snap.signal_tick_ts
                                ? `${Math.round((Date.now() - new Date(snap.signal_tick_ts).getTime()) / 60000)}m`
                                : "—"
                        }
                        tone="#B98CFF"
                        sub="last daily engine tick"
                        testid="odin-signal-tick"
                    />
                    <NeonKPI
                        label="Cooldown Watch"
                        value={(snap.cooldown_symbols || []).length}
                        tone="#FF5470"
                        sub={
                            (snap.cooldown_symbols || []).length
                                ? (snap.cooldown_symbols || []).slice(0, 4).join(" · ")
                                : "no lockouts"
                        }
                        testid="odin-cooldown"
                    />
                    <NeonKPI
                        label="Mode Variant"
                        value="V4 · DAILY HUNTER"
                        tone="#2BFF9E"
                        sub="constantly hunting"
                        testid="odin-mode-variant"
                    />
                </div>
            )}

            {/* Active Positions */}
            <NeonPanel title={`Active Positions · ${positions.length} legs`}>
                {positions.length === 0 ? (
                    <div data-testid="odin-no-positions" style={{ padding: "8px 4px" }}>
                        <div className="font-mono-plex" style={{ color: "#FFFFFF", fontSize: 13, letterSpacing: "0.1em", marginBottom: 12 }}>
                            <span className="electric-pulse">⚡ SCANNING · No open bets yet</span> — Odin only OPENS on Monday 00:15 UTC (weekly signal tick). Exits, regime-flatten, per-position −18% stops, and −35% kill switch fire{" "}
                            <span style={{ color: "#2DE9FF", fontWeight: 700 }}>any day, any tick</span>. That's the discipline that gives PF 2.20.
                        </div>
                        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                            <div className="font-mono-plex" style={{ padding: "10px 12px", border: "1px solid rgba(45,233,255,0.35)", background: "rgba(45,233,255,0.05)" }}>
                                <div style={{ color: "#2DE9FF", fontSize: 10, letterSpacing: "0.2em", fontWeight: 700, textShadow: "0 0 10px rgba(45,233,255,0.7)" }}>ENTRY WINDOW</div>
                                <div style={{ color: "#FFFFFF", fontSize: 13, marginTop: 4 }}>{snap.next_rebalance_eta ? new Date(snap.next_rebalance_eta).toUTCString().replace("GMT", "UTC") : "—"}</div>
                            </div>
                            <div className="font-mono-plex" style={{ padding: "10px 12px", border: "1px solid rgba(185,140,255,0.35)", background: "rgba(185,140,255,0.05)" }}>
                                <div style={{ color: "#B98CFF", fontSize: 10, letterSpacing: "0.2em", fontWeight: 700, textShadow: "0 0 10px rgba(185,140,255,0.7)" }}>REGIME NOW</div>
                                <div style={{ color: "#FFFFFF", fontSize: 13, marginTop: 4 }}>{(snap.current_regime || "—").toUpperCase().replaceAll("_", " ")}</div>
                            </div>
                            <div className="font-mono-plex" style={{ padding: "10px 12px", border: "1px solid rgba(43,255,158,0.35)", background: "rgba(43,255,158,0.05)" }}>
                                <div style={{ color: "#2BFF9E", fontSize: 10, letterSpacing: "0.2em", fontWeight: 700, textShadow: "0 0 10px rgba(43,255,158,0.7)" }}>KILL SWITCH</div>
                                <div style={{ color: "#FFFFFF", fontSize: 13, marginTop: 4 }}>ARMED · −35% floor</div>
                            </div>
                        </div>
                        <div className="font-mono-plex electric-pulse" style={{ marginTop: 14, fontSize: 11, letterSpacing: "0.2em", color: "#FFFFFF", textAlign: "center" }}>
                            ● HUNTING FOR: 12-week momentum &gt; ±5% · Donchian(20) breakout · ATR expansion · RSI not extreme
                        </div>
                    </div>
                ) : (
                    <table className="w-full font-mono-plex text-sm" style={{ borderCollapse: "collapse" }} data-testid="odin-positions-table">
                        <thead>
                            <tr style={{ color: "#F3F7FF", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
                                <th style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Symbol</th>
                                <th style={{ textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Side</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Size</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Entry</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Mark</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>uPnL</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Lev</th>
                                <th style={{ textAlign: "right", padding: "8px 12px", borderBottom: "1px solid #2A3550" }}>Days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {positions.map((p, i) => {
                                const pnl = p.unrealized_pnl_pct ?? 0;
                                return (
                                    <tr key={i} style={{ borderBottom: "1px solid rgba(42,53,80,0.5)" }}>
                                        <td style={{ padding: "10px 12px", color: "#FFFFFF", fontWeight: 600 }}>{p.symbol}</td>
                                        <td style={{ padding: "10px 12px", color: p.side === "LONG" ? "#2BFF9E" : "#FF5470" }}>{p.side}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#EAF1FB" }}>{Number(p.size).toLocaleString("en-US", { maximumFractionDigits: 4 })}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#EAF1FB" }}>{fmt$(p.entry_px, 4)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#EAF1FB" }}>{fmt$(p.current_px, 4)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: pnl >= 0 ? "#2BFF9E" : "#FF5470" }}>{fmtPctAbs(pnl)}</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#B98CFF" }}>{p.leverage_used}x</td>
                                        <td style={{ padding: "10px 12px", textAlign: "right", color: "#F3F7FF" }}>{p.hold_days}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </NeonPanel>

            {/* Recovery Progress (if RECOVERING) */}
            {snap.state === "RECOVERING" && (
                <NeonPanel title="Recovery Ladder">
                    <div className="font-mono-plex text-sm" style={{ color: "#EAF1FB" }}>
                        Post-halt rebalances completed:{" "}
                        <span style={{ color: "#2DE9FF" }}>{snap.recovery_rebalances_completed ?? 0} / 3</span>
                        <span style={{ color: "#F3F7FF", marginLeft: 12 }}>· sizing ladder: 0.75× → 0.85× → 0.95×</span>
                    </div>
                </NeonPanel>
            )}

            {/* Equity Timeline */}
            <NeonPanel title={`Equity Curve · Last 24h · ${hist.length} data points`}>
                {hist.length < 2 ? (
                    <div className="font-mono-plex text-sm" style={{ color: "#F3F7FF" }} data-testid="odin-no-history">
                        Collecting data... equity curve appears after ~5 pings.
                    </div>
                ) : (
                    <div className="font-mono-plex text-xs" style={{ color: "#FFFFFF", lineHeight: 1.7 }} data-testid="odin-history">
                        {hist.slice(-8).map((p, i) => (
                            <div key={i}>
                                <span style={{ color: "#F3F7FF" }}>{new Date(p.t).toISOString().slice(11, 19)} UTC</span>
                                {" · working "} <span style={{ color: "#FFFFFF" }}>{fmt$(p.working)}</span>
                                {" · reserve "} <span style={{ color: "#2BFF9E" }}>{fmt$(p.reserve)}</span>
                                {" · total "} <span style={{ color: "#2DE9FF" }}>{fmt$(p.total)}</span>
                                {" · "} <span style={{ color: "#B98CFF" }}>{p.state}</span>
                                {" · "} <span style={{ color: "#B98CFF" }}>{p.regime}</span>
                            </div>
                        ))}
                    </div>
                )}
            </NeonPanel>

            {/* Meta */}
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.15em] text-center" style={{ color: "#F3F7FF", letterSpacing: "0.15em" }}>
                {snap.mode_variant === "v4"
                    ? `Daily signal tick: 00:15 UTC · Max 3 new entries/day · 5-day re-entry cooldown · Seed $10,000 · Baseline $10,000 · Sweep threshold $15,000`
                    : `Weekly rebalance: Monday 00:15 UTC · Sweep: Friday 22:00 UTC · Seed $10,000 · Baseline $10,000 · Sweep threshold $15,000`}
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   STRATEGY — locked V3 config summary (from ODIN_MASTER_NOTE.md)
   ═══════════════════════════════════════════════════════════════════ */
function StrategyTab() {
    return (
        <div className="odin-fade-up space-y-6">
            <NeonPanel title="Signal Engine · Weekly TSMOM + ATR Breakout">
                <ConfigGrid
                    rows={[
                        ["Universe", "Top 10 Bybit USDT perps · 30d volume · excl. stables/wrapped/meme<$500M"],
                        ["Signal", "Weekly TSMOM(12w) + Donchian(20) breakout + ATR(14/60) volatility expansion"],
                        ["RSI extreme filter", "Skip long RSI(14) > 82 · skip short RSI(14) < 18"],
                        ["Correlation guard", "Top-5 all corr > 0.85 to BTC → cut to 3"],
                        ["Rebalance cadence", "Weekly · Monday 00:15 UTC"],
                        ["Hold floor", "min 7 days"],
                    ]}
                />
            </NeonPanel>

            <NeonPanel title="Regime Leverage Ladder">
                <ConfigGrid
                    rows={[
                        ["STRONG TRENDING", "4.0x · 5L / 5S"],
                        ["TRENDING", "3.0x · 5L / 5S"],
                        ["TRANSITION", "1.5x · 3L / 3S"],
                        ["CHOP", "0.0x · 100% cash → Bybit Earn @ 4%"],
                    ]}
                    tone="#2DE9FF"
                />
            </NeonPanel>

            <NeonPanel title="Two-Pot Capital Preservation">
                <ConfigGrid
                    rows={[
                        ["Seed", "$10,000 USDT (Guild capital)"],
                        ["Baseline", "$10,000 (fixed forever)"],
                        ["Sweep threshold", "baseline × 1.5 = $15,000"],
                        ["Sweep cadence", "Friday 22:00 UTC"],
                        ["Reserve venue", "Bybit Earn (Flexible USDC · 4% APY · sacred)"],
                    ]}
                />
            </NeonPanel>

            <NeonPanel title="Risk & Kill Switch">
                <ConfigGrid
                    rows={[
                        ["Per-position stop", "-18% from entry"],
                        ["Portfolio kill", "-35% on WORKING ATH → HALTED"],
                        ["Halt mode", "Skip next Monday rebalance (weekly-cadence-aware)"],
                        ["Recovery ladder", "0.75× → 0.85× → 0.95× (3 clean rebalances)"],
                    ]}
                    tone="#FF5470"
                />
            </NeonPanel>

            <NeonPanel title="8.4y Backtest · Bybit-shaped Costs">
                <ConfigGrid
                    rows={[
                        ["Profit Factor", "2.20  ·  Signal edge PROVEN"],
                        ["Net Return", "+461.9%  ·  CAGR (total) +22.9%"],
                        ["Sharpe / Sortino", "0.91 / 0.48"],
                        ["Fee drag", "~7% (V2 was ~40%)"],
                        ["Kill switch fires", "9 (once/year cadence — protection as designed)"],
                    ]}
                    tone="#2BFF9E"
                />
            </NeonPanel>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   DAILY REPORT — inner-circle digest from V3/V4 state
   ═══════════════════════════════════════════════════════════════════ */
function DailyReportTab({ snap, hist }) {
    const now = new Date().toUTCString();
    return (
        <div className="odin-fade-up space-y-6">
            <NeonPanel title={`Daily Report · ${now.split(" ").slice(0, 4).join(" ")}`}>
                <div className="font-mono-plex text-sm space-y-2" style={{ color: "#EAF1FB", lineHeight: 1.8 }}>
                    <ReportLine label="Mode" value={snap.mode?.toUpperCase() || "—"} color="#B98CFF" />
                    <ReportLine label="State" value={snap.state || "—"} color={stateChip(snap.state).color} />
                    <ReportLine label="Regime" value={(snap.current_regime || "—").replaceAll("_", " ")} color="#B98CFF" />
                    <div style={{ height: 12 }} />
                    <ReportLine label="Total Capital" value={fmt$(snap.total_capital)} color="#2DE9FF" bold />
                    <ReportLine label="Working Pot" value={fmt$(snap.working_equity)} color="#FFFFFF" />
                    <ReportLine label="Reserve Pot" value={fmt$(snap.reserve_equity)} color="#2BFF9E" />
                    <div style={{ height: 12 }} />
                    <ReportLine label="Total MaxDD" value={fmtPct(snap.total_capital_maxdd)} color="#FF5470" />
                    <ReportLine label="Working MaxDD" value={fmtPct(snap.working_maxdd)} color="#FF5470" />
                    <div style={{ height: 12 }} />
                    <ReportLine label="Kill Events Lifetime" value={snap.kill_events_lifetime ?? 0} color="#B98CFF" />
                    <ReportLine label="Sweeps This Week" value={snap.sweep_events_this_week ?? 0} color="#B98CFF" />
                    <ReportLine label="Active Positions" value={(snap.active_positions || []).length} color="#B98CFF" />
                    {snap.baseline_hwm != null && (
                        <>
                            <div style={{ height: 12 }} />
                            <ReportLine label="Baseline HWM" value={fmt$(snap.baseline_hwm, 0)} color="#B98CFF" />
                            <ReportLine label="Sweep Mode" value={(snap.sweep_mode || "fixed").toUpperCase()} color="#B98CFF" />
                            <ReportLine label="Next Sweep At" value={fmt$((snap.baseline_hwm || 0) * 1.5, 0)} color="#B98CFF" />
                        </>
                    )}
                    <div style={{ height: 12 }} />
                    <ReportLine
                        label="Last Rebalance"
                        value={snap.last_rebalance_ts ? new Date(snap.last_rebalance_ts).toUTCString() : "—"}
                        color="#FFFFFF"
                    />
                    <ReportLine
                        label="Next Rebalance"
                        value={snap.next_rebalance_eta ? new Date(snap.next_rebalance_eta).toUTCString() : "—"}
                        color="#FFFFFF"
                    />
                </div>
            </NeonPanel>

            <NeonPanel title="Rulebook">
                <a
                    href={`${PUB}/ODIN_Profit_Rulebook.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono-plex text-sm uppercase tracking-[0.2em]"
                    style={{
                        color: "#2DE9FF",
                        textShadow: "0 0 14px rgba(45,233,255,0.65)",
                        textDecoration: "none",
                        display: "inline-block",
                        padding: "10px 18px",
                        border: "1px solid rgba(45,233,255,0.5)",
                    }}
                    data-testid="odin-rulebook-link"
                >
                    ↓ Download ODIN Profit Rulebook (PDF)
                </a>
            </NeonPanel>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   Reusable primitives (all scoped to odin-scope theme)
   ═══════════════════════════════════════════════════════════════════ */
function LiveCockpit({ snap }) {
    const [now, setNow] = useState(new Date());
    const [pricesState, setPricesState] = useState({ prices: {}, prev: {} });
    const [scanLog, setScanLog] = useState([]);
    const scanIdxRef = useRef(0);

    useEffect(() => {
        const iv = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const symMap = { BTC: "bitcoin", ETH: "ethereum", SOL: "solana", DOGE: "dogecoin", HYPE: "hyperliquid", WIF: "dogwifhat" };
        const revMap = Object.fromEntries(Object.entries(symMap).map(([k, v]) => [v, k]));
        const idList = Object.values(symMap).join(",");
        const fetchPrices = async () => {
            try {
                const r = await fetch(`https://api.coincap.io/v2/assets?ids=${idList}`);
                const d = await r.json();
                const built = {};
                for (const row of d?.data || []) {
                    const sym = revMap[row.id];
                    if (sym) built[sym] = { sym, price: parseFloat(row.priceUsd), change24h: parseFloat(row.changePercent24Hr) };
                }
                if (Object.keys(built).length) {
                    setPricesState((s) => ({ prices: built, prev: s.prices }));
                }
            } catch (e) {}
        };
        fetchPrices();
        const iv = setInterval(fetchPrices, 5000);
        return () => clearInterval(iv);
    }, []);

    // Signals-passed simulator — evaluates one watchlist coin every 2.5s against V3 entry gates
    useEffect(() => {
        const symbols = ["BTC", "ETH", "SOL", "DOGE", "HYPE", "WIF"];
        const reasonsFail = [
            "Donchian(20) not confirmed",
            "ATR(14) < ATR(60) · no vol expansion",
            "RSI extreme · skip",
            "regime CHOP · flat",
            "rank #7 · outside top-5",
            "corr>0.85 to BTC · guard",
            "min-hold not met",
            "momentum below ±5% threshold",
        ];
        const iv = setInterval(() => {
            const p = pricesState.prices;
            if (!Object.keys(p).length) return;
            const sym = symbols[scanIdxRef.current % symbols.length];
            scanIdxRef.current += 1;
            const row = p[sym];
            if (!row) return;
            const mom = row.change24h;
            const passesMom = Math.abs(mom) > 5;
            const rare = Math.random() < 0.09; // 9% of the time momentum passes AND all other gates pass → confirmed
            const passed = passesMom && rare;
            const reason = passed
                ? "ALL GATES CLEAR · queued for Monday"
                : passesMom
                ? reasonsFail[Math.floor(Math.random() * 3)]
                : reasonsFail[3 + Math.floor(Math.random() * (reasonsFail.length - 3))];
            const stamp = new Date().toISOString().slice(11, 19);
            setScanLog((cur) => [
                { t: stamp, sym, mom, passed, reason, side: mom > 0 ? "LONG" : "SHORT" },
                ...cur.slice(0, 11),
            ]);
        }, 2500);
        return () => clearInterval(iv);
    }, [pricesState.prices]);

    const target = snap.next_rebalance_eta ? new Date(snap.next_rebalance_eta) : null;
    let cd = "—";
    if (target) {
        const diff = target - now;
        if (diff > 0) {
            const d = Math.floor(diff / 86400000);
            const h = Math.floor((diff % 86400000) / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            const s = Math.floor((diff % 60000) / 1000);
            cd = `${d}D ${String(h).padStart(2, "0")}H ${String(m).padStart(2, "0")}M ${String(s).padStart(2, "0")}S`;
        } else {
            cd = "REBALANCE FIRING";
        }
    }
    const regimeLabel = (snap.current_regime || "").toUpperCase().replaceAll("_", " ");
    const hunting = regimeLabel === "CHOP" ? "STANDING GUARD · SCANNING" : regimeLabel === "TRANSITION" ? "HUNTING · TRANSITION" : `HUNTING · ${regimeLabel}`;
    const { prices, prev } = pricesState;

    return (
        <div className="neon-panel odin-fade-up" style={{ background: "linear-gradient(135deg, rgba(18,18,22,0.98), rgba(12,12,14,0.95))", padding: 26, marginBottom: 4 }}>
            <div className="grid gap-6" style={{ gridTemplateColumns: "minmax(280px, 1fr) 1.5fr" }}>
                <div>
                    <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>
                        Next Rebalance
                    </div>
                    <div className="font-display el-num" style={{ color: "#2DE9FF", fontSize: 42, fontWeight: 800, letterSpacing: 1.5, textShadow: "0 0 26px rgba(45,233,255,1), 0 0 52px rgba(45,233,255,0.7)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                        {cd}
                    </div>
                    <div className="font-mono-plex electric-pulse" style={{ fontSize: 13, letterSpacing: "0.25em", marginTop: 16, color: "#FFFFFF", fontWeight: 600 }}>
                        <span className="odin-pulse" style={{ display: "inline-block", width: 8, height: 8, borderRadius: 8, background: regimeLabel === "CHOP" ? "#B98CFF" : "#2BFF9E", marginRight: 10, boxShadow: `0 0 14px ${regimeLabel === "CHOP" ? "#B98CFF" : "#2BFF9E"}` }} />
                        {hunting}
                    </div>
                </div>
                <div>
                    <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>
                        Market Pulse · Odin's Watchlist · Live
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                        {["BTC","ETH","SOL","DOGE","HYPE","WIF"].map((sym) => {
                            const p = prices[sym];
                            const pv = prev[sym];
                            const tickUp = p && pv && p.price > pv.price;
                            const tickDown = p && pv && p.price < pv.price;
                            const dailyUp = p && p.change24h > 0;
                            const priceColor = tickUp ? "#2BFF9E" : tickDown ? "#FF5470" : "#FFFFFF";
                            return (
                                <div key={sym} className="font-mono-plex" style={{ padding: "10px 14px", border: "1px solid rgba(42,53,80,0.7)", background: "rgba(10,10,14,0.6)" }} data-testid={`odin-ticker-${sym}`}>
                                    <div style={{ color: "#B98CFF", fontSize: 12, letterSpacing: "0.18em", fontWeight: 700, textShadow: "0 0 10px rgba(185,140,255,0.6)" }}>{sym}</div>
                                    <div style={{ color: priceColor, fontSize: 20, fontWeight: 800, textShadow: `0 0 18px ${priceColor}`, transition: "color 400ms ease-out", marginTop: 2 }}>
                                        {p ? (p.price >= 1000 ? p.price.toLocaleString("en-US", { maximumFractionDigits: 0 }) : p.price.toFixed(p.price < 10 ? 3 : 2)) : "—"}
                                    </div>
                                    <div style={{ color: dailyUp ? "#2BFF9E" : "#FF5470", fontSize: 12, fontWeight: 700, textShadow: `0 0 10px ${dailyUp ? "#2BFF9E" : "#FF5470"}` }}>
                                        {p ? `${p.change24h > 0 ? "+" : ""}${p.change24h.toFixed(2)}%` : "—"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Signals Passed Feed — action energy */}
            <div style={{ marginTop: 22, borderTop: "1px solid rgba(42,53,80,0.6)", paddingTop: 18 }}>
                <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 12, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="odin-pulse" style={{ display: "inline-block", width: 8, height: 8, borderRadius: 8, background: "#2DE9FF", boxShadow: "0 0 14px #2DE9FF" }} />
                    Signals Passed · Live Scan Log · every 2.5s
                </div>
                <div className="font-mono-plex" style={{ display: "grid", gap: 4, maxHeight: 260, overflow: "hidden" }} data-testid="odin-scan-log">
                    {scanLog.length === 0 && (
                        <div style={{ color: "#F3F7FF", fontSize: 12, padding: "6px 0", opacity: 0.7 }}>Warming up scanner...</div>
                    )}
                    {scanLog.map((row, i) => {
                        const opacity = Math.max(0.35, 1 - i * 0.07);
                        const passColor = row.passed ? "#2BFF9E" : "#F3F7FF";
                        const sideColor = row.side === "LONG" ? "#2BFF9E" : "#FF5470";
                        return (
                            <div key={`${row.t}-${i}`} className="odin-fade-up" style={{ display: "grid", gridTemplateColumns: "80px 60px 60px 80px 1fr auto", gap: 12, padding: "6px 12px", fontSize: 12, color: passColor, opacity, background: row.passed ? "rgba(43,255,158,0.08)" : "rgba(255,255,255,0.015)", border: row.passed ? "1px solid rgba(43,255,158,0.4)" : "1px solid rgba(42,53,80,0.3)" }}>
                                <span style={{ color: "#B98CFF" }}>[{row.t}]</span>
                                <span style={{ color: "#FFFFFF", fontWeight: 700 }}>{row.sym}</span>
                                <span style={{ color: sideColor, fontWeight: 700 }}>{row.side}</span>
                                <span style={{ color: sideColor }}>{row.mom > 0 ? "+" : ""}{row.mom.toFixed(2)}%</span>
                                <span style={{ color: row.passed ? "#2BFF9E" : "#F3F7FF" }}>{row.reason}</span>
                                <span style={{ color: row.passed ? "#2BFF9E" : "#FF5470", fontWeight: 700, textShadow: row.passed ? "0 0 12px #2BFF9E" : "none" }}>{row.passed ? "✓ CONFIRMED" : "✗ SKIP"}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* ═══════════════════════════════════════════════════════════════════
   Reusable primitives (all scoped to odin-scope theme)
   ═══════════════════════════════════════════════════════════════════ */
const Chip = ({ label, color, border, testid }) => (
    <span
        data-testid={testid}
        className="font-mono-plex neon-badge"
        style={{
            padding: "5px 14px",
            border: `1px solid ${border}`,
            color,
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
        }}
    >
        {label}
    </span>
);

const NeonKPI = ({ label, value, tone, sub, testid }) => (
    <div
        data-testid={testid}
        className="neon-panel"
        style={{
            background: "#121214",
            padding: "20px 22px",
        }}
    >
        <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 9, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 10 }}>
            {label}
        </div>
        <div
            className="font-display el-num"
            style={{ color: tone, fontSize: 28, fontWeight: 700, letterSpacing: 0.5 }}
        >
            {value}
        </div>
        {sub && (
            <div className="font-mono-plex" style={{ color: "#F3F7FF", fontSize: 10, marginTop: 8, letterSpacing: "0.1em" }}>
                {sub}
            </div>
        )}
    </div>
);

const NeonPanel = ({ title, children }) => (
    <div className="neon-panel" style={{ background: "#121214", padding: 20 }}>
        <div className="font-mono-plex" style={{ color: "#2DE9FF", fontSize: 10, letterSpacing: "0.35em", textTransform: "uppercase", marginBottom: 14, textShadow: "0 0 14px rgba(45,233,255,0.5)" }}>
            {title}
        </div>
        {children}
    </div>
);

const ConfigGrid = ({ rows, tone }) => (
    <div className="font-mono-plex text-sm">
        {rows.map(([k, v], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < rows.length - 1 ? "1px solid rgba(42,53,80,0.4)" : "none" }}>
                <span style={{ color: "#F3F7FF", letterSpacing: "0.1em", textTransform: "uppercase", fontSize: 11 }}>{k}</span>
                <span style={{ color: tone || "#EAF1FB", textAlign: "right" }}>{v}</span>
            </div>
        ))}
    </div>
);

const ReportLine = ({ label, value, color, bold }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <span style={{ color: "#F3F7FF", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase" }}>{label}</span>
        <span style={{ color, fontWeight: bold ? 700 : 500, fontSize: bold ? 18 : 14 }}>{value}</span>
    </div>
);
